import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Paper, Typography, Chip } from '@mui/material';
import { Link as ApiIcon, Lock as AuthIcon } from '@mui/icons-material';
import { WorkflowNode } from '../../../../types/workflow.types';

const ApiCallNode: React.FC<NodeProps<WorkflowNode['data']>> = ({ data, selected }) => {
  const config = data?.config || {};

  const getMethodColor = (method?: string) => {
    switch (method?.toUpperCase()) {
      case 'GET': return '#4CAF50';
      case 'POST': return '#2196F3';
      case 'PUT': return '#FF9800';
      case 'DELETE': return '#F44336';
      case 'PATCH': return '#9C27B0';
      default: return '#666666';
    }
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      style={{
        padding: 16,
        minWidth: 150,
        maxWidth: 280,
        backgroundColor: '#9C27B0',
        color: 'white',
        border: selected ? '3px solid #1976d2' : '1px solid #7B1FA2',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          backgroundColor: '#7B1FA2',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ApiIcon style={{ fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold">
          {data?.title || 'API Call'}
        </Typography>
      </div>

      {data?.description && (
        <Typography variant="caption" style={{ opacity: 0.9, fontSize: '0.7rem', marginBottom: 8, display: 'block' }}>
          {data?.description}
        </Typography>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {config?.method && (
          <Chip
            label={config?.method?.toUpperCase() || ''}
            size="small"
            style={{
              backgroundColor: getMethodColor(config?.method),
              color: 'white',
              fontSize: '0.6rem',
              height: 16,
              fontWeight: 'bold',
            }}
          />
        )}

        {config?.authentication && config.authentication.type !== 'none' && (
          <Chip
            icon={<AuthIcon style={{ fontSize: 12, color: 'white' }} />}
            label={config?.authentication?.type?.toUpperCase() || ''}
            size="small"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '0.6rem',
              height: 16,
            }}
          />
        )}
      </div>

      {config?.url && (
        <Typography
          variant="caption"
          style={{
            opacity: 0.8,
            fontSize: '0.65rem',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            marginBottom: 4,
            display: 'block'
          }}
        >
          {config?.url && config.url.length > 40 ? config.url.substring(0, 40) + '...' : config?.url}
        </Typography>
      )}

      {config?.timeout && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.6rem' }}>
          Timeout: {config?.timeout}s
        </Typography>
      )}

      {!config?.url && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.65rem' }}>
          Click to configure API endpoint
        </Typography>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          backgroundColor: '#7B1FA2',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />
    </Paper>
  );
};

export default ApiCallNode;
